import httpClient from 'api/httpClient';
import publicHttpClient from 'api/publicHttpClient';
import {
  ActivateAgreementBody,
  AgreementExecutionRole,
  AgreementExecutionSummary,
  AgreementPaginated,
  AgreementPaginationParams,
  AgreementSigningSession,
  AgreementTemplate,
  SignAgreementBody,
} from 'models/Agreement';
import { AxiosRequestConfig } from 'axios';
import credentialsService from 'services/credentialsService';

function getListingAgreement(listingId: string) {
  return httpClient.get<AgreementTemplate | null>(
    `agreements/listings/${listingId}`,
  );
}

function activateAgreement(
  listingId: string,
  { file, fields, messageToBuyers }: ActivateAgreementBody,
) {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('fields', JSON.stringify(fields));

  if (messageToBuyers) formData.append('messageToBuyers', messageToBuyers);

  return httpClient.post<AgreementTemplate>(
    `agreements/listings/${listingId}/activate`,
    formData,
  );
}

function deactivateListingAgreement(listingId: string) {
  return httpClient.delete(`agreements/listings/${listingId}`);
}

function getListingAgreementExecutions(
  listingId: string,
  params?: AgreementPaginationParams,
) {
  return httpClient.get<AgreementPaginated<AgreementExecutionSummary>>(
    `agreements/listings/${listingId}/executions`,
    { params },
  );
}

function getExecutionIntegrity(executionId: string) {
  return httpClient.get<{
    storedSha256: string;
    recordSha256: string;
    match: boolean;
  }>(`agreements/executions/${executionId}/integrity`);
}

const SIGNING_REQUEST_CONFIG = {
  __skipGlobalForbiddenHandling: true,
} as AxiosRequestConfig;

const getSigningHttpClient = () =>
  credentialsService.token ? httpClient : publicHttpClient;

function getSigningSession(token: string) {
  return getSigningHttpClient().get<AgreementSigningSession>(
    `agreement-signing/${token}`,
    SIGNING_REQUEST_CONFIG,
  );
}

function sendSigningCode(token: string) {
  return publicHttpClient.post<{ sentTo: string; resendAvailableAt: string }>(
    `agreement-signing/${token}/code`,
  );
}

function verifySigningCode(token: string, code: string) {
  return publicHttpClient.post<{ emailVerified: true }>(
    `agreement-signing/${token}/code/verify`,
    { code },
  );
}

function signAgreement(token: string, body: SignAgreementBody) {
  return getSigningHttpClient().post<{
    executionId: string;
    executedSha256: string;
    signedAt: string;
  }>(`agreement-signing/${token}/sign`, body, SIGNING_REQUEST_CONFIG);
}

function getSignedAgreementDownloadUrl(token: string) {
  return publicHttpClient.get<{ url: string }>(
    `agreement-signing/${token}/download`,
  );
}

function getLeadSigningLink(leadId: string) {
  return httpClient.get<{ url: string }>(
    `agreements/leads/${leadId}/signing-link`,
  );
}

function getMyAgreementExecutions(
  params: AgreementPaginationParams & { role: AgreementExecutionRole },
) {
  return httpClient.get<AgreementPaginated<AgreementExecutionSummary>>(
    'agreements/executions/my',
    { params },
  );
}

function getExecutionDownloadUrl(executionId: string) {
  return httpClient.get<{ url: string }>(
    `agreements/executions/${executionId}/download`,
  );
}

export default {
  getListingAgreement,
  activateAgreement,
  deactivateListingAgreement,
  getListingAgreementExecutions,
  getExecutionIntegrity,
  getSigningSession,
  sendSigningCode,
  verifySigningCode,
  signAgreement,
  getSignedAgreementDownloadUrl,
  getLeadSigningLink,
  getMyAgreementExecutions,
  getExecutionDownloadUrl,
};
